import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Col, Form, Input, Radio, Row } from 'antd';
import { autoGeneratePassword, bulkPasswordReset, resetLinkforBulkUsers } from 'redux/slices/resetPassword';
import { SweetSweetAlert } from 'component/sweetalert_components';
import { StyledButton } from 'component/button_components/button_general/ButtonGeneralStyle';
import { FormSubmitButtonConatainer } from 'component/form_component/style';
import { Link } from 'react-router-dom';
import ModalOpenLoader from 'component/loader_component/modalOpenLoader';
import { setSelectedItems } from 'redux/slices/userSlice';

export default function BulkResetPassword({ setBulkResetModal }) {
  const dispatch = useDispatch();
  const selectedNode = useSelector((state) => state.selectedNode);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(false);
  const [showForm, setShowForm] = useState('random');
  const { selectedItems, selectedItemsValues } = useSelector((state) => state.getUsers);
  const { mailResetPassword, overrideGlobalStatus, domainMailResetPassword } = useSelector((state) => state.settings);
  const [passwordAtNextLogon, setPasswordAtNextLogon] = useState(true);
  const canNotify = overrideGlobalStatus?.settings ? domainMailResetPassword ?? true : mailResetPassword ?? true;

  const onFinish = async (values) => {
    if (showForm === 'random' || showForm === 'manual') {
      try {
        setLoading(true);
        const passwordValues = selectedItemsValues.map((password) => ({
          strUserName: password.dn,
          strNewPassword: showForm === 'manual' ? values.strNewPassword : '',
          passwordAtNextLogon: passwordAtNextLogon,
          notifyEmail: values.notifyEmail,
          fqdn: selectedNode.domainName,
          domainId: selectedNode.currentdomainId,
        }));
        const response = await dispatch(bulkPasswordReset(passwordValues));
        if (
          response?.payload.data ===
          'Something went wrong!! Considering changing the AD connection protocol to SSL or TLS'
        ) {
          await SweetSweetAlert({
            text: response.payload.data,
            icon: 'success',
            buttonColor: 'green',
          });
        } else {
          await SweetSweetAlert({
            sweetData: `Password Reset has been successful for the selected ${
              selectedItems.length === 1 ? 'user' : 'users'
            }.`,
            icon: 'success',
            buttonColor: 'green',
          });
        }
      } catch (error) {
        await SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
      } finally {
        setLoading(false);
        dispatch(setSelectedItems([]));
        setBulkResetModal(false);
      }
    } else if (showForm === 'link') {
      try {
        setLoading(true);
        const { protocol, hostname, port } = window.location;
        const currentDomain = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
        const linkValues = selectedItemsValues.map((value) => ({
          strEmail: value.email,
          strUser: value.samAccountName,
          domainName: selectedNode.domainName.split('.')[0],
        }));
        let resetData = {
          links: linkValues,
          resetUrl: currentDomain,
          domainId: selectedNode.currentdomainId,
        };
        const response = await dispatch(resetLinkforBulkUsers(resetData));
        if (
          response?.payload.data ===
          'Something went wrong!! Considering changing the AD connection protocol to SSL or TLS'
        ) {
          await SweetSweetAlert({
            text: response.payload.data,
            icon: 'error',
            buttonColor: 'red',
          });
        } else {
          await SweetSweetAlert({
            sweetData: `The link has been successfully sent to the selected ${
              selectedItems.length === 1 ? 'user' : 'users'
            }.`,
            icon: 'success',
            buttonColor: 'green',
          });
        }
      } catch (error) {
        await SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
      } finally {
        setLoading(false);
        dispatch(setSelectedItems([]));
        setBulkResetModal(false);
      }
    }
  };

  const generatePassword = async () => {
    const passwordValue = await dispatch(autoGeneratePassword(selectedNode.currentdomainId));
    if (passwordValue?.payload.status === 200) {
      return passwordValue.payload.data;
    }
  };
  const handleGeneratePassword = async () => {
    const newPassword = await generatePassword();
    form.setFieldsValue({
      strNewPassword: newPassword,
      confirm_password: newPassword,
    });
    setPasswordMatch(true);
  };

  const validatePasswordMatch = async (_, value) => {
    const newPassword = form.getFieldValue('strNewPassword');
    if (newPassword && value === newPassword) {
      setPasswordMatch(true);
      return Promise.resolve();
    } else {
      setPasswordMatch(false);
      throw new Error('Passwords do not match');
    }
  };

  const onRadioChange = (e) => {
    setShowForm(e.target.value);
  };

  return (
    <>
      {loading && <ModalOpenLoader />}
      <Row className='af-bulk-reset-wrap'>
        <Col span={24} className='af-bulk-reset-subwrap'>
          <Form
            size='large'
            labelCol={{ span: 7 }}
            form={form}
            name='register'
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{
              strNewPassword: '',
              fqdn: selectedNode.domainName,
              passwordAtNextLogon: passwordAtNextLogon,
              notifyEmail: canNotify,
            }}
            className='af-bulk-reset-form-element'
          >
            <h2 className='af-bulk-reset-heading'>Reset Password</h2>

            <Form.Item name='radio-button' style={{ margin: '10px 0px' }} className='af-bulk-reset-form-item-radio'>
              <Radio.Group onChange={onRadioChange} defaultValue='random' className='af-bulk-reset-radio-group'>
                <Radio className='af-bulk-reset-radio-random' value='random'>
                  Random Password
                </Radio>
                <Radio className='af-bulk-reset-radio-manual' value='manual' style={{ marginBottom: '10px' }}>
                  Manual
                </Radio>
                <Radio className='af-bulk-reset-radio-link' value='link'>
                  Send Reset Link
                </Radio>
              </Radio.Group>
            </Form.Item>
            {showForm === 'manual' && (
              <>
                <Form.Item
                  name='strNewPassword'
                  label='New Password'
                  rules={[
                    {
                      required: true,
                      message: 'Please Enter Password',
                    },
                  ]}
                  style={{ marginBottom: 10 }}
                  className='af-bulk-reset-form-item-newpass'
                >
                  <Input.Password className='af-bulk-reset-input-newpass' />
                </Form.Item>
                <Form.Item
                  name='confirm_password'
                  label='Confirm Password'
                  dependencies={['strNewPassword']}
                  rules={[
                    {
                      required: true,
                      message: 'Please confirm your password',
                    },
                    {
                      validator: validatePasswordMatch,
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                  className='af-bulk-reset-form-item-confirmpass'
                >
                  <Input.Password className='af-bulk-reset-input-confirmpass' />
                </Form.Item>
                <Form.Item
                  label='generate'
                  name='generate'
                  className='hidden_label visible-hidden af-bulk-reset-form-item-generate'
                  style={{ marginBottom: 10 }}
                >
                  <Link className='af-bulk-reset-generate-link' onClick={handleGeneratePassword}>
                    Generate
                  </Link>
                </Form.Item>
              </>
            )}
            {showForm !== 'link' && (
              <>
                <Form.Item
                  label='nextlogon'
                  key='passwordAtNextLogon'
                  name='passwordAtNextLogon'
                  className='hidden_label display-none af-bulk-reset-form-item-nextlogon'
                  style={{ marginBottom: '0px' }}
                >
                  <Checkbox
                    checked={passwordAtNextLogon}
                    onChange={(e) => setPasswordAtNextLogon(e.target.checked)}
                    className='af-bulk-reset-nextlogon-checkbox'
                  >
                    Force user to change password on next login.
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  label='Notify by email'
                  key='notifyByMail'
                  name='notifyEmail'
                  className='hidden_label display-none af-bulk-reset-form-item-notify'
                  valuePropName='checked'
                >
                  <Checkbox checked={canNotify} className='af-bulk-reset-notify-checkbox'>
                    Notify by email
                  </Checkbox>
                </Form.Item>
              </>
            )}

            <FormSubmitButtonConatainer className='af-bulk-reset-form-item-submit'>
              <StyledButton
                className='af-bulk-reset-submit-button'
                type='primary'
                htmlType='submit'
                disabled={!passwordMatch && showForm === 'manual'}
              >
                Submit
              </StyledButton>
            </FormSubmitButtonConatainer>
          </Form>
        </Col>
      </Row>
    </>
  );
}
